// Import Dependencies
import React, { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { getFormByIdWebhook, submitFormWebhook, transformAPIFormToPreviewFormat, sendPhoneOTP, verifyPhoneOTP } from "services/formBuilderService";
import sensationCrm from "assets/images/Sensation-Logo-v2.svg"
// Import Phone Input
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import enLocale from 'react-phone-number-input/locale/en.json';
// Import scrollbar styles
import "../../../styles/scrollbar.css";
// Import custom styles for FormPage
import "./FormPage.css";

// UI Components
import { Button, Input, Textarea, Select, Checkbox, Radio, SearchableSelect } from "components/ui";

// Toast notifications
import { toast } from "sonner";
import { JWT_HOST_API } from "configs/auth.config";
import { contactService } from 'services/contactService';

// Import Validation
import * as Yup from "yup";
import formValidationSchema from "components/leads/LeadActions/QuickLeadValidation";

// ----------------------------------------------------------------------

export function FormPage(props) {
  const { formId: paramFormId, assignTo } = useParams();
  const formId = paramFormId || props.formId;
  const assignToUser = assignTo || props.assignTo;
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Phone OTP verification states
  const [phoneFieldId, setPhoneFieldId] = useState(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpId, setOtpId] = useState(null); // Store OtpId from API response
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [canResendOtp, setCanResendOtp] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(sensationCrm);

  // Static fields
  const [colleges, setColleges] = useState([]);
  const [streams, setStreams] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const [collegesRes, streamsRes, sessionsRes] = await Promise.all([
          contactService.getColleges(),
          contactService.getStreams(),
          contactService.getSessions()
        ]);
        if (collegesRes?.data) {
          setColleges(typeof collegesRes.data === 'string' ? JSON.parse(collegesRes.data) : collegesRes.data);
        }
        if (streamsRes?.data) {
          setStreams(typeof streamsRes.data === 'string' ? JSON.parse(streamsRes.data) : streamsRes.data);
        }
        if (sessionsRes?.data) {
          setSessions(typeof sessionsRes.data === 'string' ? JSON.parse(sessionsRes.data) : sessionsRes.data);
        }
      } catch (err) {
        console.error("Failed to fetch static data", err);
      }
    };
    fetchStaticData();
  }, []);

  // Set document title and enforce light theme for public form
  useEffect(() => {
    document.title = "Form";

    // Public form should always render in light mode regardless of phone/browser dark mode
    const html = document.documentElement;
    const hadDark = html.classList.contains("dark");
    html.classList.remove("dark");
    html.style.colorScheme = "light";

    return () => {
      if (hadDark) {
        html.classList.add("dark");
      }
      html.style.colorScheme = "";
    };
  }, []);

  // Set company logo from URL
  useEffect(() => {
    const setCompanyLogoFromUrl = () => {
      try {
        const hostname = window.location.hostname;
        let folderName = hostname;

        // Handle localhost development environment
        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
          folderName = 'localhost';
        }

        const apiHost = JWT_HOST_API.endsWith('/') ? JWT_HOST_API : `${JWT_HOST_API}/`;
        const logoUrl = `${apiHost}CompanyLogos/${folderName}/main.png`;

        setCompanyLogo(logoUrl);
      } catch (error) {
        console.error("Failed to set company logo from URL", error);
      }
    };
    setCompanyLogoFromUrl();
  }, []);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        if (!formId) {
          setError('No form ID provided');
          setLoading(false);
          return;
        }

        const result = await getFormByIdWebhook(formId);

        if (result.success && result.data) {
          // Check if form is active before processing
          const isActive = result.data.IsActive !== undefined ? result.data.IsActive : true;

          // Check if data is null/empty (missing FormId)
          if (!result.data.FormId && !result.data.formId) {
            setError('This form is currently unavailable');
            return;
          }

          if (!isActive) {
            setError('This form is currently unavailable');
            return;
          }

          // Use the shared transformation function to match preview format
          const transformedForm = transformAPIFormToPreviewFormat(result.data);
          setForm(transformedForm);
        } else {
          // Handle different error types

          // Check multiple ways a 401/unauthorized could be indicated
          const isUnauthorized =
            result.statusCode === 401 ||
            result.message === "Unauthorised" ||
            result.message === "Unauthorized" ||
            (result.error && result.error.status === 401) ||
            (result.error && result.error.statusCode === 401) ||
            (result.error && (
              result.error.message === "Unauthorised" ||
              result.error.message === "Unauthorized"
            ));

          if (isUnauthorized) {
            setError('access_denied');
          } else {
            setError('Form not found');
          }
        }
      } catch (err) {
        console.error('Error fetching form:', err);

        // Check if the error is a 401 unauthorized error
        const isUnauthorized =
          err.response?.status === 401 ||
          err.response?.statusCode === 401 ||
          err.message?.includes('401') ||
          err.message?.includes('Unauthorised') ||
          err.message?.includes('Unauthorized');

        if (isUnauthorized) {
          setError('access_denied');
        } else {
          setError('Failed to load form: ' + (err.message || 'Unknown error'));
        }
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      fetchForm();
    } else {
      setError('No form ID found in URL');
      setLoading(false);
    }
  }, [formId]);

  // Find phone field and initialize defaults when form loads
  useEffect(() => {
    if (form) {
      // Find all fields from all steps
      const allFields = form.isMultistep
        ? form.steps?.flatMap(step => step.fields) || []
        : form.fields || [];

      // identify phone field by FieldTypeId = 10
      const phoneField = allFields.find(field => {
        const fieldTypeId = field.fieldTypeId || field.FieldTypeId;
        return fieldTypeId === 10;
      });

      if (phoneField) {
        setPhoneFieldId(phoneField.id);
      }

      // Initialize Country default to India
      const countryField = allFields.find(f => {
        const lowerLabel = (f.label || f.fieldLabel || '').toLowerCase();
        return lowerLabel === 'country' || lowerLabel === 'countryid';
      });

      if (countryField) {
        // If it's a select/radio field, look for India option
        const indiaOption = countryField.options?.find(opt => 
          (opt.label || '').toLowerCase() === 'india'
        );
        
        // Use matching option value, or ID, or just 'India'
        const defaultValue = indiaOption ? indiaOption.value : 'India';

        setFormValues(prev => {
          // Only set if not already set by user
          if (prev[countryField.id]) return prev;
          return {
            ...prev,
            [countryField.id]: defaultValue
          };
        });
      }
    }
  }, [form]);

  // Resend OTP timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResendOtp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle sending OTP
  const handleSendOtp = async () => {
    const phoneNumber = formValues[phoneFieldId];

    if (!phoneNumber) {
      setOtpError('Please enter a phone number first');
      toast.error('Please enter a phone number');
      return;
    }

    // Validate using the library's robust validation (checks country code + local length)
    if (!isValidPhoneNumber(phoneNumber)) {
      setOtpError('Please enter a valid phone number');
      toast.error('Please enter a valid phone number');
      return;
    }

    // Extract only digits from international format for the API payload
    // e.g., "+91 98765 43210" -> "919876543210"
    const fullPhoneNumber = phoneNumber.replace(/\D/g, '');

    setIsSendingOtp(true);
    setOtpError('');

    try {
      // Pass existing otpId for resend, or empty for first send
      // Send full number with country code
      const result = await sendPhoneOTP(fullPhoneNumber, otpId || '');

      if (result.success) {
        setOtpSent(true);
        setOtpId(result.otpId); // Store the OtpId for verification
        setCanResendOtp(false);
        setResendTimer(30); // 30 seconds cooldown
        setShowOtpModal(true); // Open OTP modal
        toast.success('OTP sent successfully!', {
          description: 'Please check your phone for the verification code.',
          duration: 4000,
        });
      } else {
        setOtpError(result.message || 'Failed to send OTP');
        toast.error(result.message || 'Failed to send OTP');
      }
    } catch (err) {
      setOtpError('Failed to send OTP. Please try again.');
      toast.error('Failed to send OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle verifying OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpError('Please enter a valid OTP');
      return;
    }

    if (!otpId) {
      setOtpError('Invalid verification session. Please request a new OTP.');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      const result = await verifyPhoneOTP(otpId, otp);

      if (result.success) {
        setIsPhoneVerified(true);
        setOtpSent(false);
        setShowOtpModal(false); // Close modal on success
        toast.success('Phone number verified successfully!', {
          duration: 3000,
        });
      } else {
        setOtpError(result.message || 'Invalid OTP');
        toast.error(result.message || 'Invalid OTP');
      }
    } catch (err) {
      setOtpError('Failed to verify OTP. Please try again.');
      toast.error('Failed to verify OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Get schema key from label for validation
  const getSchemaKey = (label) => {
    const lowerLabel = (label || '').toLowerCase();
    if (lowerLabel.includes('first name') || lowerLabel === 'first_name' || lowerLabel === 'firstname') {
      return 'firstName';
    } else if (lowerLabel.includes('last name') || lowerLabel === 'last_name' || lowerLabel === 'lastname') {
      return 'lastName';
    } else if (lowerLabel.includes('email')) {
      return 'email';
    } else if (lowerLabel.includes('phone') || lowerLabel.includes('mobile') || lowerLabel.includes('contact')) {
      return 'phone';
    } else if (lowerLabel.includes('country')) {
      return 'country';
    } else if (lowerLabel.includes('semester')) {
      return 'semester';
    }
    return null;
  };

  // Validate a single field
  const validateField = async (fieldId, label, value) => {
    try {
      const schemaKey = getSchemaKey(label);

      if (schemaKey && formValidationSchema.fields[schemaKey]) {
        // Use prioritized Yup schema validation
        await formValidationSchema.fields[schemaKey].validate(value);

        // Clear error if passes
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });
      } else {
        // Fallback to basic required validation if no schema rule
        const allFields = form.isMultistep
          ? form.steps?.flatMap(step => step.fields) || []
          : form.fields || [];

        const field = allFields.find(f => f.id === fieldId);

        if (field?.required) {
          let isEmpty = false;
          if (field.type === 'checkbox') {
            isEmpty = !value || (Array.isArray(value) && value.length === 0);
          } else if (typeof value === 'string') {
            isEmpty = !value.trim();
          } else {
            isEmpty = value === undefined || value === null || value === '';
          }

          if (isEmpty) {
            throw new Error(`${label} is required`);
          }
        }

        // Clear error if basic check passes
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });
      }
    } catch (err) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldId]: err.message
      }));
    }
  };

  const handleFieldChange = (fieldId, value) => {
    // Sanitize value to prevent HTML elements and events from being stored
    let sanitizedValue = value;

    if (typeof value === 'object' && value !== null) {
      // Check if it's a React SyntheticEvent
      if (value.nativeEvent && value.target && typeof value.preventDefault === 'function') {
        // Extract value from SyntheticEvent
        sanitizedValue = value.target.value;
        console.warn(`Field ${fieldId} received SyntheticEvent, extracted value:`, sanitizedValue);
      } else if (value instanceof Element || value instanceof Node) {
        // Extract value from HTML elements
        sanitizedValue = value.value || value.textContent || String(value);
        console.warn(`Field ${fieldId} received HTML element, extracted value:`, sanitizedValue);
      }
    }

    // Find the field to check its label for mobile number validation
    const field = form?.fields?.find(f => f.id === fieldId) ||
      form?.steps?.flatMap(step => step.fields)?.find(f => f.id === fieldId);

    // Check if this is a PhoneInput field (OTP field)
    const isPhoneInputField = fieldId === phoneFieldId;

    // Mobile number validation - only for NON-PhoneInput fields (the regular digit-only fields)
    if (!isPhoneInputField && field && sanitizedValue && typeof sanitizedValue === 'string') {
      const fieldLabel = field.label?.toLowerCase() || '';
      const isMobileField = fieldLabel.includes('mobile') ||
        fieldLabel.includes('phone') ||
        (fieldLabel.includes('number') && !fieldLabel.includes('order') && !fieldLabel.includes('reference'));
      const isSemesterField = fieldLabel.includes('semester');

      if (isMobileField) {
        // Remove non-digit characters and limit to 10 digits
        const digitsOnly = sanitizedValue.replace(/\D/g, '');
        if (digitsOnly.length > 10) {
          sanitizedValue = digitsOnly.slice(0, 10);
        } else {
          sanitizedValue = digitsOnly;
        }
      } else if (isSemesterField) {
        sanitizedValue = sanitizedValue.replace(/\D/g, '');
      }
    }

    // Reset phone verification if phone number changes
    if (fieldId === phoneFieldId && (isPhoneVerified || otpSent)) {
      setIsPhoneVerified(false);
      setOtpSent(false);
      setOtp('');
      setOtpId(null);
      setOtpError('');
    }

    setFormValues(prev => ({
      ...prev,
      [fieldId]: sanitizedValue
    }));

    // Trigger real-time validation if the field was previously invalid or has something changing
    if (field) {
      validateField(fieldId, field.label, sanitizedValue);
    }
  };

  // Improved validation for a set of fields using Yup schema
  const validateFormFields = async (fields) => {
    let isValid = true;
    const errors = {};

    for (const field of fields) {
      const value = formValues[field.id];
      const schemaKey = getSchemaKey(field.label);

      try {
        if (schemaKey && formValidationSchema.fields[schemaKey]) {
          await formValidationSchema.fields[schemaKey].validate(value);
        } else if (field.required) {
          let isEmpty = false;
          if (field.type === 'checkbox') {
            isEmpty = !value || (Array.isArray(value) && value.length === 0);
          } else if (typeof value === 'string') {
            isEmpty = !value.trim();
          } else {
            isEmpty = value === undefined || value === null || value === '';
          }

          if (isEmpty) {
            throw new Error(`${field.label} is required`);
          }
        }
      } catch (err) {
        errors[field.id] = err.message;
        isValid = false;
      }
    }

    setFormErrors(prev => ({ ...prev, ...errors }));
    
    return isValid;
  };

  const handleNextStep = async () => {
    if (form.isMultistep && form.steps[currentStep]) {
      // Validate current step's fields before moving to next
      const currentStepFields = form.steps[currentStep].fields || [];
      const isValid = await validateFormFields(currentStepFields);

      if (!isValid) {
        toast.error('Please correct the errors before proceeding', {
          duration: 3000,
        });
        return;
      }
    }

    if (currentStep < form.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate all required fields before submission
    const allFields = form.isMultistep
      ? form.steps?.flatMap(step => step.fields) || []
      : form.fields || [];


    const isValid = await validateFormFields(allFields);
    if (!isValid) {
      toast.error('Please correct the errors before submitting', {
        duration: 3000,
      });
      return;
    }

    // Check if phone verification is required and not completed
    // if (phoneFieldId && !isPhoneVerified) {
    //   toast.error('Phone verification required', {
    //     description: 'Please verify your phone number before submitting the form.',
    //     duration: 5000,
    //   });
    //   return;
    // }

    setIsSubmitting(true);

    try {
      // Find College, Stream, Semester from dynamic fields
      const finalFormValues = {
         ...formValues
      };

      allFields.forEach(f => {
        const lower = (f.label || '').toLowerCase();
        if (lower.includes('college') && formValues[f.id]) {
          finalFormValues.collegeId = formValues[f.id];
        }
        if ((lower.includes('stream') || lower.includes('branch')) && formValues[f.id]) {
          finalFormValues.streamId = formValues[f.id];
        }
        if (lower.includes('semester') && formValues[f.id]) {
          finalFormValues.semester = formValues[f.id];
        }
      });

      const result = await submitFormWebhook(form.id, finalFormValues, assignToUser, form.categoryId, form);

      if (result.success) {
        // Navigate to the Thank You page, passing the leadId from API response
        const leadId = result.leadId || result.data?.leadId || '';
        navigate(`/form-thankyou/${leadId}`);
      } else {
        toast.error(result.message || 'Failed to submit form', {
          description: 'Please check your information and try again.',
          duration: 5000,
        });
      }
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to submit form', {
        description: 'An unexpected error occurred. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const fieldValue = formValues[field.id] || (field.type === 'checkbox' ? [] : '');

    // Check if this is THE phone field that requires OTP verification (by FieldTypeId = 10)
    const fieldTypeId = field.fieldTypeId || field.FieldTypeId;
    const isPhoneOtpField = field.id === phoneFieldId && fieldTypeId === 10;

    return (
      <div key={field.id} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {(field.type === 'text' || field.type === 'email' || field.type === 'number' || field.type === 'date' || field.type === 'password') && (
          <div>
            {/* Phone field with OTP verification - using PhoneInput with country code dropdown */}
            {isPhoneOtpField ? (
              <div className="relative">
                <PhoneInput
                  international
                  defaultCountry="IN"
                  value={fieldValue && fieldValue.startsWith('+') ? fieldValue : (fieldValue ? `+${fieldValue}` : '')}
                  onChange={(value) => handleFieldChange(field.id, value || '')}
                  onCountryChange={(countryCode) => {
                    if (countryCode && enLocale[countryCode]) {
                      const countryName = enLocale[countryCode];
                      let matchedFieldId = null;
                      let matchedValue = null;

                      const allFields = form.isMultistep
                        ? form.steps?.flatMap(step => step.fields) || []
                        : form.fields || [];

                      const cField = allFields.find(f => {
                        const lower = f.label?.toLowerCase() || '';
                        return lower === 'country' || lower === 'countryid';
                      });

                      if (cField) {
                        matchedFieldId = cField.id;
                        const opts = cField.options || [];
                        const opt = opts.find(o => 
                          (o.label || '').toLowerCase() === countryName.toLowerCase()
                        );
                        matchedValue = opt ? opt.value : countryName;
                      }

                      if (matchedFieldId && matchedValue) {
                        handleFieldChange(matchedFieldId, matchedValue);
                      }
                    }
                  }}
                  placeholder={field.placeholder || 'Enter phone number'}
                  className="phone-input-custom"
                  style={{
                    '--PhoneInputCountryFlag-borderRadius': form.styling?.borderRadius || '4px',
                  }}
                />
                {/* Verify/Verified text below input */}
                {/* Verify/Verified text below input */}
                {/* <div className="mt-2 flex items-center justify-between">
                  {isPhoneVerified ? (
                    <span className="text-green-600 text-sm font-medium flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Verified
                    </span>
                  ) : fieldValue && isValidPhoneNumber(fieldValue) ? (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp}
                      className="text-sm font-medium px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-50"
                      style={{
                        backgroundColor: form.styling?.primaryColor || "#3B82F6",
                        color: 'white',
                        borderRadius: form.styling?.borderRadius || '8px'
                      }}
                    >
                      {isSendingOtp ? 'Sending OTP...' : 'Verify Phone Number'}
                    </button>
                  ) : null}
                </div> */}
              </div>
            ) : (
              /* Regular text/email/number/date/password fields */
              <Input
                type={field.type}
                value={fieldValue}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
                className="w-full"
                style={{ borderRadius: form.styling?.borderRadius || '8px' }}
              />
            )}
          </div>
        )}

        {field.type === 'textarea' && (
          <Textarea
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
            className="w-full"
            style={{ borderRadius: form.styling?.borderRadius || '8px' }}
          />
        )}

        {field.type === 'select' && (() => {
          const lowerLabel = (field.label || '').toLowerCase();
          const isCollege = lowerLabel.includes('college');
          const isStream = lowerLabel.includes('stream') || lowerLabel.includes('branch');
          const isSession = lowerLabel.includes('session');
          let optionsToRender = field.options || [];

          if (isCollege) {
            if (colleges && colleges.length > 0) {
              optionsToRender = colleges.map(c => ({
                value: c.CollegeId ?? c.OptionValue ?? c.id ?? c.value,
                label: c.OptionLabel || c.CollegeName || c.label || c.name || String(c.CollegeId)
              }));
            }
          } else if (isStream) {
            if (streams && streams.length > 0) {
              optionsToRender = streams.map(s => ({
                value: s.StreamId ?? s.OptionValue ?? s.id ?? s.value,
                label: s.StreamName || s.OptionLabel || s.label || s.name || String(s.StreamId)
              }));
            }
          } else if (isSession) {
            if (sessions && sessions.length > 0) {
              optionsToRender = sessions.map(s => ({
                value: s.OptionValue ?? s.SessionName ?? s.id ?? s.value,
                label: s.OptionLabel || s.SessionName || s.label || s.name
              }));
            }
          }

          // Deduplicate and sanitize options
          const seen = new Set();
          const uniqueOptions = [];
          (optionsToRender || []).forEach(opt => {
            const rawVal = typeof opt === 'object' ? (opt?.value ?? opt?.OptionValue ?? opt?.id) : opt;
            const rawLabel = typeof opt === 'object' ? (opt?.label ?? opt?.OptionLabel ?? opt?.StreamName ?? opt?.CollegeName) : opt;
            if (rawVal !== undefined && rawVal !== null && !seen.has(String(rawVal))) {
              seen.add(String(rawVal));
              uniqueOptions.push({
                value: rawVal,
                label: rawLabel || String(rawVal)
              });
            }
          });

          // Render SearchableSelect for all select fields to ensure consistent styling and prevent phone OS black color
          return (
            <SearchableSelect
              id={`field-${field.id}`}
              value={fieldValue}
              onChange={(selectedValue) => handleFieldChange(field.id, selectedValue)}
              options={uniqueOptions}
              placeholder={`Select ${field.label}`}
              searchPlaceholder="Search"
              required={field.required}
              error={Boolean(formErrors[field.id])}
              className="w-full"
              style={{ borderRadius: form.styling?.borderRadius || '8px' }}
            />
          );
        })()}

        {field.type === 'checkbox' && (
          <div className="space-y-2 p-1">
            {field.options?.map(option => {
              const isChecked = fieldValue.includes(option.value);
              return (
                <label key={option.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors duration-150">
                  <div className="relative mr-3">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...fieldValue, option.value]
                          : fieldValue.filter(v => v !== option.value);
                        handleFieldChange(field.id, newValues);
                      }}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${isChecked
                      ? 'border-blue-600'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                      }`}
                      style={{
                        backgroundColor: isChecked ? (form.styling?.primaryColor || '#3B82F6') : 'white',
                        borderColor: isChecked ? (form.styling?.primaryColor || '#3B82F6') : undefined
                      }}>
                      {isChecked && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium select-none" style={{ color: form.styling?.textColor || '#374151' }}>
                    {option.label}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {field.type === 'radio' && (
          <div className="space-y-2 p-1">
            {field.options?.map(option => (
              <label key={option.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors duration-150">
                <div className="relative mr-3">
                  <input
                    type="radio"
                    name={`field-${field.id}`}
                    value={option.value}
                    checked={fieldValue === option.value}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${fieldValue === option.value
                    ? 'border-blue-600'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                    style={{
                      backgroundColor: fieldValue === option.value ? (form.styling?.primaryColor || '#3B82F6') : 'white',
                      borderColor: fieldValue === option.value ? (form.styling?.primaryColor || '#3B82F6') : undefined
                    }}>
                    {fieldValue === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
                <span className="text-sm font-medium select-none" style={{ color: form.styling?.textColor || '#374151' }}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        )}
        
        {/* Error Message */}
        {formErrors[field.id] && (
          <p className="text-red-500 text-xs mt-1 animate-fadeIn flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {formErrors[field.id]}
          </p>
        )}
      </div>
    );
  };

  // Loading state removed - now handled by global loader

  if (error) {
    // Different styling for different error types
    const isInactive = error === 'This form is currently unavailable';
    const isAccessDenied = error === 'access_denied';

    let iconColor, bgColor, title, message, helpText;

    if (isAccessDenied) {
      iconColor = 'text-red-600';
      bgColor = 'bg-red-100';
      title = 'Access Denied';
      message = 'You are not authorized to view this form from this domain.';
      helpText = 'Please contact the form owner to authorize your domain or ensure you are accessing the form from the correct website.';
    } else if (isInactive) {
      iconColor = 'text-orange-600';
      bgColor = 'bg-orange-100';
      title = 'Form Unavailable';
      message = error;
      helpText = 'Please contact the form owner for more information.';
    } else {
      iconColor = 'text-red-600';
      bgColor = 'bg-red-100';
      title = 'Form Not Found';
      message = error;
      helpText = null;
    }

    return (
      <div className="h-screen overflow-y-auto bg-gray-50 flex items-center justify-center form-page-container">
        <div className="text-center max-w-md mx-auto px-4 py-12">
          <div className={`w-16 h-16 ${bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {isAccessDenied ? (
              <svg className={`w-8 h-8 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : isInactive ? (
              <svg className={`w-8 h-8 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
              </svg>
            ) : (
              <svg className={`w-8 h-8 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            )}
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600 mb-3">{message}</p>
          {helpText && (
            <p className="text-sm text-gray-500">{helpText}</p>
          )}
        </div>
      </div>
    );
  }


  if (!form) {
    return null;
  }

  const currentStepData = form.isMultistep ? form.steps[currentStep] : null;
  const fieldsToRender = form.isMultistep ? currentStepData?.fields || [] : form.fields || [];

  return (
    <div className="h-screen overflow-y-auto bg-gray-50 py-8 px-4 form-page-container">
      <div className="max-w-2xl mx-auto">
        <div
          className="bg-white rounded-lg shadow-lg p-8 mb-12"
          style={{
            backgroundColor: form.styling?.backgroundColor || '#ffffff',
            borderRadius: form.styling?.borderRadius || '8px',
            color: form.styling?.textColor || '#1f2937'
          }}
        >
          {/* Form Header */}
          <div className="mb-8">
            <h1
              className="text-3xl font-bold mb-4"
              style={{ color: form.styling?.primaryColor || '#3B82F6' }}
            >
              {form.title}
            </h1>
            {form.description && (
              <p className="text-gray-600 mb-6">{form.description}</p>
            )}

            {/* Progress Bar for Multistep */}
            {form.isMultistep && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{currentStepData?.stepTitle}</span>
                  <span className="text-xs text-gray-500">Step {currentStep + 1} of {form.steps.length}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${((currentStep + 1) / form.steps.length) * 100}%`,
                      backgroundColor: form.styling?.primaryColor || '#3B82F6'
                    }}
                  />
                </div>
                {currentStepData?.stepDescription && (
                  <p className="text-sm text-gray-600 mt-2">{currentStepData.stepDescription}</p>
                )}
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            {fieldsToRender.map(renderField)}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {form.isMultistep && currentStep > 0 && (
              <button
                onClick={handlePreviousStep}
                className="px-4 py-2 text-gray-600 border border-gray-300 font-medium rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
                style={{ borderRadius: form.styling?.borderRadius || "8px" }}
              >
                Previous
              </button>
            )}

            <div className="flex-1" />

            {form.isMultistep ? (
              currentStep < form.steps.length - 1 ? (
                <button
                  onClick={handleNextStep}
                  className="px-4 py-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity cursor-pointer"
                  style={{
                    backgroundColor: form.styling?.primaryColor || "#3B82F6",
                    borderRadius: form.styling?.borderRadius || "8px"
                  }}
                >
                  Next
                </button>
              ) : (
                <div className="flex flex-col items-end gap-2">
                  {/* {phoneFieldId && !isPhoneVerified && (
                    <p className="text-xs text-orange-600">Please verify your phone number to submit</p>
                  )} */}
                  <button
                    onClick={handleSubmit}
                    // disabled={isSubmitting || (phoneFieldId && !isPhoneVerified)}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: form.styling?.primaryColor || "#3B82F6",
                      borderRadius: form.styling?.borderRadius || "8px"
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Form'}
                  </button>
                </div>
              )
            ) : (
              <div className="flex flex-col items-end gap-2">
                {/* {phoneFieldId && !isPhoneVerified && (
                  <p className="text-xs text-orange-600">Please verify your phone number to submit</p>
                )} */}
                <button
                  onClick={handleSubmit}
                  // disabled={isSubmitting || (phoneFieldId && !isPhoneVerified)}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: form.styling?.primaryColor || "#3B82F6",
                    borderRadius: form.styling?.borderRadius || "8px"
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Form'}
                </button>
              </div>
            )}
          </div>

          {/* Branding */}
          <div className="mt-8 pt-4 border-t border-gray-200 text-center">
            <p className="text-[14px] text-gray-500 flex justify-center items-center gap-2">
              Powered by  <img src={companyLogo} className="h-8" alt="logo" onError={(e) => { e.target.src = sensationCrm }} />
            </p>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal - Using Headless UI Dialog */}
      <Transition appear show={showOtpModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-100 flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
          onClose={() => {

          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 transition-opacity dark:bg-black/40" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="scrollbar-sm relative flex w-full max-w-md flex-col overflow-y-auto rounded-lg bg-white px-6 py-8 text-center transition-all duration-300 dark:bg-dark-700">
              {/* Close button */}
              <button
                type="button"
                onClick={() => {
                  setShowOtpModal(false);
                  setOtpError('');
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-dark-300 dark:hover:text-dark-100 cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal Header */}
              <div className="mb-6">
                <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-7 h-7 text-primary-600 dark:text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <DialogTitle
                  as="h3"
                  className="text-xl font-semibold text-gray-800 dark:text-dark-100"
                >
                  Verify Phone Number
                </DialogTitle>
                <p className="mt-2 text-sm text-gray-500 dark:text-dark-300">
                  Enter the OTP sent to your phone
                </p>
              </div>

              {/* OTP Input */}
              <div className="space-y-4">
                <Input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                    setOtpError('');
                  }}
                  placeholder="Enter OTP"
                  className="w-full text-center text-lg tracking-widest"
                  maxLength={6}
                  autoFocus
                />

                {otpError && (
                  <p className="text-sm text-error dark:text-error-lighter text-center">{otpError}</p>
                )}

                {/* Verify Button */}
                <Button
                  onClick={handleVerifyOtp}
                  disabled={isVerifyingOtp || !otp || otp.length < 4}
                  className="w-full"
                  color="primary"
                >
                  {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                </Button>

                {/* Resend OTP */}
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-dark-300">
                    Didn&apos;t receive the code?{' '}
                    {canResendOtp ? (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp}
                        className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:underline cursor-pointer disabled:opacity-50"
                      >
                        {isSendingOtp ? 'Sending...' : 'Resend OTP'}
                      </button>
                    ) : (
                      <span className="text-gray-400 dark:text-dark-400">Resend in {resendTimer}s</span>
                    )}
                  </p>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </div>
  );
}
